import { Fragment, type ReactNode } from "react";
import {
  Box,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { AssistantCabinetLink } from "./AssistantCabinetLink";
import { isCabinetPath, isHttpUrl } from "./safeUrl";

/**
 * Крошечный безопасный markdown ответов помощника партнёра (порт
 * site/src/features/assistant/lib/renderAssistantMarkdown.tsx плюс
 * таблицы и заголовки). Всё идёт текстом через React, который экранирует
 * его сам: HTML из ответа модели в DOM не попадает. Ссылки: пути /partner
 * через роутер на месте, http(s) в новой вкладке, прочие схемы текстом.
 */

// **жирный** | `код` | [текст](url), в url допускается одна пара скобок.
const INLINE_RE =
  /\*\*([\s\S]+?)\*\*|`([^`\n]+)`|\[([^\]\n]+)\]\(([^()\s]+(?:\([^()\s]*\)[^()\s]*)?)\)/g;
const BULLET_RE = /^\s*[-*•]\s+(.*)$/;
const ORDERED_RE = /^\s*\d+[.)]\s+(.*)$/;
const HEADING_RE = /^\s{0,3}#{1,6}\s+(.*?)\s*#*\s*$/;
const FENCE_RE = /^\s*```/;
const isTableRow = (l: string) => /^\s*\|.*\|\s*$/.test(l);
const isSeparator = (l: string) =>
  /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?\s*$/.test(l);
const cells = (l: string) =>
  l
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((c) => c.trim());
/** Число, сумма или процент: "42", "$1 200.50", "12,5 %", "-3", "1 000 ₽". */
const isNumeric = (c: string) =>
  /^[^\d\s-]{0,3}\s?[-+−]?\d[\d\s.,]*\s?(%|[^\d\s]{1,3})?$/.test(
    c.replace(/\*\*/g, "")
  );
const isListLine = (l: string) => BULLET_RE.test(l) || ORDERED_RE.test(l);
/** Маркер списка без текста: так пункт выглядит посреди потока ("- "). */
const isBareMarker = (l: string) => /^\s*(?:[-*•]|\d+[.)])\s*$/.test(l);

function renderInline(text: string, key: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let i = 0;
  for (const m of text.matchAll(INLINE_RE)) {
    const start = m.index ?? 0;
    if (start > last) out.push(text.slice(last, start));
    const k = `${key}-${i++}`;
    const [, bold, code, label, href] = m;
    if (bold !== undefined) {
      out.push(<strong key={k}>{renderInline(bold, k)}</strong>);
    } else if (code !== undefined) {
      out.push(
        <Box
          key={k}
          component="code"
          sx={{
            px: 0.5,
            py: 0.125,
            borderRadius: 0.5,
            bgcolor: "action.hover",
            fontFamily: "monospace",
            fontSize: "0.9em",
          }}
        >
          {code}
        </Box>
      );
    } else if (label !== undefined && href !== undefined) {
      if (isCabinetPath(href)) {
        out.push(
          <AssistantCabinetLink key={k} to={href}>
            {label}
          </AssistantCabinetLink>
        );
      } else if (isHttpUrl(href)) {
        out.push(
          <Link
            key={k}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
          >
            {label}
          </Link>
        );
      } else {
        out.push(<Fragment key={k}>{label}</Fragment>);
      }
    }
    last = start + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function renderTable(head: string[], rows: string[][], k: string): ReactNode {
  const width = head.length;
  const body = rows.map((r) =>
    Array.from({ length: width }, (_, j) => r[j] ?? "")
  );
  const numeric = head.map(
    (_, j) =>
      body.length > 0 &&
      body.every((r) => !r[j] || isNumeric(r[j] ?? "")) &&
      body.some((r) => !!r[j])
  );
  return (
    <TableContainer
      key={k}
      sx={{
        my: 1,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        overflowX: "auto",
      }}
    >
      <Table
        size="small"
        sx={{
          "& th, & td": { py: 0.75, px: 1.25, fontSize: "0.8125rem" },
          "& tbody tr:last-of-type td": { borderBottom: 0 },
          "& tbody tr:hover": { bgcolor: "action.hover" },
        }}
      >
        <TableHead>
          <TableRow sx={{ bgcolor: "action.hover" }}>
            {head.map((h, j) => (
              <TableCell
                key={j}
                align={numeric[j] ? "right" : "left"}
                sx={{
                  fontWeight: 600,
                  color: "text.secondary",
                  whiteSpace: "nowrap",
                }}
              >
                {renderInline(h, `${k}h${j}`)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {body.map((r, ri) => (
            <TableRow key={ri}>
              {r.map((c, j) => (
                <TableCell
                  key={j}
                  align={numeric[j] ? "right" : "left"}
                  sx={
                    numeric[j]
                      ? {
                          whiteSpace: "nowrap",
                          fontVariantNumeric: "tabular-nums",
                        }
                      : undefined
                  }
                >
                  {renderInline(c, `${k}r${ri}c${j}`)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/**
 * Ответ помощника: абзацы, заголовки, **жирный**, `код`, [ссылки](...),
 * списки, блоки кода, таблицы markdown (цифры больше трёх строк, п. 9 спеки).
 */
export function renderAssistantMarkdown(text: string): ReactNode {
  const lines = text.replace(/\r\n?/g, "\n").split("\n");
  const at = (j: number) => lines[j] ?? "";
  const blocks: ReactNode[] = [];
  let i = 0;
  let n = 0;
  while (i < lines.length) {
    const line = at(i);
    if (!line.trim() || isBareMarker(line)) {
      i++;
      continue;
    }
    const k = `b${n++}`;

    if (FENCE_RE.test(line)) {
      const code: string[] = [];
      i++;
      while (i < lines.length && !FENCE_RE.test(at(i))) code.push(at(i++));
      i++;
      blocks.push(
        <Box
          key={k}
          component="pre"
          sx={{
            my: 1,
            p: 1.25,
            borderRadius: 1,
            bgcolor: "action.hover",
            fontFamily: "monospace",
            fontSize: "0.8125rem",
            overflowX: "auto",
            whiteSpace: "pre",
          }}
        >
          {code.join("\n")}
        </Box>
      );
      continue;
    }

    if (isTableRow(line) && i + 1 < lines.length && isSeparator(at(i + 1))) {
      const head = cells(line);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && isTableRow(at(i))) {
        rows.push(cells(at(i++)));
      }
      blocks.push(renderTable(head, rows, k));
      continue;
    }

    const heading = HEADING_RE.exec(line)?.[1];
    if (heading !== undefined) {
      i++;
      blocks.push(
        <Typography
          key={k}
          variant="subtitle2"
          component="p"
          sx={{ fontWeight: 600, mt: 1.5, mb: 0.5 }}
        >
          {renderInline(heading, k)}
        </Typography>
      );
      continue;
    }

    if (isListLine(line)) {
      const ordered = !BULLET_RE.test(line);
      const re = ordered ? ORDERED_RE : BULLET_RE;
      const items: string[] = [];
      while (i < lines.length && re.test(at(i))) {
        const item = at(i++).replace(re, "$1");
        if (item.trim()) items.push(item);
      }
      if (!items.length) continue;
      blocks.push(
        <Box
          key={k}
          component={ordered ? "ol" : "ul"}
          sx={{ pl: 2.5, my: 0.75, "& > li + li": { mt: 0.25 } }}
        >
          {items.map((it, j) => (
            <Typography key={j} component="li" variant="body2">
              {renderInline(it, `${k}-${j}`)}
            </Typography>
          ))}
        </Box>
      );
      continue;
    }

    const para: string[] = [];
    while (
      i < lines.length &&
      at(i).trim() &&
      !isTableRow(at(i)) &&
      !isListLine(at(i)) &&
      !isBareMarker(at(i)) &&
      !HEADING_RE.test(at(i)) &&
      !FENCE_RE.test(at(i))
    ) {
      para.push(at(i++));
    }
    if (!para.length) {
      // Строка таблицы без разделителя: показываем как текст.
      para.push(at(i++));
    }
    blocks.push(
      <Typography key={k} variant="body2" sx={{ my: 0.75 }}>
        {para.map((p, j) => (
          <Fragment key={j}>
            {j > 0 && <br />}
            {renderInline(p, `${k}-${j}`)}
          </Fragment>
        ))}
      </Typography>
    );
  }
  return <>{blocks}</>;
}
